import React, { useState, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { useAuthContext } from '../../../context/AuthContext';
// import apiService from '../../../services/api'; // manual auth disabled
import { ROUTES } from '../../../utils/constants';
import styles from './LoginModal.module.css';
import { saveAuth } from '../../../utils/authStorage'; // ✅ email-keyed storage
import TermsAndPrivacyModal from './TermsAndPrivacyModal'; // ✅ Terms & Privacy modal

const LoginModal = ({ isOpen, onClose, onOpenVerification, setCurrentPage }) => {
  const { login, loginWithOAuth } = useAuthContext();

  const [mode, setMode] = useState('login');
  const [loginIdentifier, setLoginIdentifier] = useState('');
  const [loginPassword, setLoginPassword] = useState('');
  const [loginError, setLoginError] = useState('');
  // loginLoading, showUseVerificationOption — removed (manual auth disabled)
  const [useVerificationCode, setUseVerificationCode] = useState(false);

  const [signupEmail, setSignupEmail] = useState('');
  const [signupPhone, setSignupPhone] = useState('');
  const [signupPassword, setSignupPassword] = useState('');
  const [signupPasswordConfirm, setSignupPasswordConfirm] = useState('');
  const [signupError, setSignupError] = useState('');
  // signupLoading — removed (manual auth disabled)
  const [verificationSent, setVerificationSent] = useState(false);
  const [verificationCode, setVerificationCode] = useState('');

  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [googleLoading, setGoogleLoading] = useState(false);
  const [facebookLoading, setFacebookLoading] = useState(false);
  const [showTerms,       setShowTerms]       = useState(false); // ✅ Terms & Privacy modal
  const [termsAgreed,     setTermsAgreed]     = useState(false); // ✅ Signup checkbox
  const [termsError,      setTermsError]      = useState('');    // ✅ Checkbox nudge message

  // ✅ FIX: Read ?ref= from URL every time the modal opens — NOT just on first
  // mount. The old useState lazy initializer ran once when the component first
  // mounted (before the user lands on the ?ref= URL if the modal is always in
  // the DOM), so refCode was always '' for re-signups via referral links.
  // Now we re-read window.location.search each time isOpen flips to true so
  // the ref code is always current regardless of when the modal was mounted.
  const [refCode, setRefCode] = useState('');
  const [shaking, setShaking] = useState(false); // ── shake inputs when user tries manual auth

  // Trigger shake animation + auto-clear after 600ms
  const triggerShake = () => {
    setShaking(true);
    setTimeout(() => setShaking(false), 620);
  };

  useEffect(() => {
    if (!isOpen) return;
    try {
      const code = new URLSearchParams(window.location.search).get('ref') || '';
      setRefCode(code);
      // ✅ Only force signup mode if there's a ref code AND user doesn't exist yet.
      // If user already has an account, checkUserExists will redirect them to login
      // inside handleSendSignupVerification — no need to pre-check here.
      if (code) {
        setMode('signup');
      }
    } catch {
      setRefCode('');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  const loginRef            = useRef(login);
  const loginWithOAuthRef   = useRef(loginWithOAuth);
  const setCurrentPageRef   = useRef(setCurrentPage);
  const onCloseRef          = useRef(onClose);

  useEffect(() => {
    loginRef.current          = login;
    loginWithOAuthRef.current = loginWithOAuth;
    setCurrentPageRef.current = setCurrentPage;
    onCloseRef.current        = onClose;
  }, [login, loginWithOAuth, setCurrentPage, onClose]);

  // ✅ BroadcastChannel — receives OAuth success/failure from the popup
  useEffect(() => {
    const channel = new BroadcastChannel('oauth_channel');

    channel.onmessage = (event) => {
      console.log('📡 BroadcastChannel message:', event.data);
      const { type, token, user, isAdmin, error } = event.data;

      if (type === 'oauth-success') {
        if (token || user) saveAuth(token, user);
        if (loginWithOAuthRef.current) loginWithOAuthRef.current(event.data);

        setGoogleLoading(false);
        setFacebookLoading(false);
        onCloseRef.current();

        try {
          const saved = localStorage.getItem('pendingProductView');
          if (saved) {
            window.dispatchEvent(new CustomEvent('restoreProductModal'));
            localStorage.removeItem('pendingProductView');
          }
        } catch (e) { /* ignore */ }

        setCurrentPageRef.current(
          (user?.is_admin || isAdmin) ? ROUTES.ADMIN : ROUTES.HOME
        );

      } else if (type === 'oauth-error') {
        setLoginError(error || 'Social login failed. Please try again.');
        setGoogleLoading(false);
        setFacebookLoading(false);
      }
    };

    return () => channel.close();
  }, []);

  if (!isOpen) return null;

  // ── Helpers ──────────────────────────────────────────────────────────────

  // handlePostLoginRedirect — unused (manual auth disabled)
  // const handlePostLoginRedirect = () => {
  //   try {
  //     const saved = localStorage.getItem('pendingProductView');
  //     if (saved) {
  //       window.dispatchEvent(new CustomEvent('restoreProductModal'));
  //       localStorage.removeItem('pendingProductView');
  //     }
  //   } catch (e) { console.error(e); }
  // };

  // const validatePassword = (p) => p.length < 6 ? 'Password must be at least 6 characters' : null; // manual auth disabled
  // const validateEmail    = (e) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(e) ? null : 'Please enter a valid email address'; // manual auth disabled

  // const switchToSignup = (prefillEmail = '') => { // manual auth disabled
  //   resetLoginForm();
  //   if (prefillEmail) setSignupEmail(prefillEmail);
  //   setMode('signup');
  // };

  // ── LOGIN HANDLER ─────────────────────────────────────────────────────────

  // ── MANUAL AUTH — disabled; shakes inputs and redirects to Google ──────────
  const MANUAL_UNAVAILABLE_MSG =
    '⬇️ Please use Continue with Google below to sign in or create your account.';

  const notifyManualUnavailable = (setError) => {
    triggerShake();
    setError(MANUAL_UNAVAILABLE_MSG);
  };

  const handlePasswordLogin        = (e) => { e?.preventDefault(); notifyManualUnavailable(setLoginError); };
  const handleSendVerificationCode = (e) => { e?.preventDefault(); notifyManualUnavailable(setLoginError); };
  const handleSendSignupVerification = (e) => { e?.preventDefault(); notifyManualUnavailable(setSignupError); };
  const handleSignup               = (e) => { e?.preventDefault(); notifyManualUnavailable(setSignupError); };

  // Also fire on any input focus/change attempt
  const handleManualInputInteraction = () => {
    if (!shaking) notifyManualUnavailable(mode === 'login' ? setLoginError : setSignupError);
  };

  // ── OAUTH ─────────────────────────────────────────────────────────────────

  const openOAuthPopup = (url, name) => {
    const width = 500, height = 600;
    const left = window.screen.width / 2 - width / 2;
    const top  = window.screen.height / 2 - height / 2;
    const popup = window.open(url, name, `width=${width},height=${height},left=${left},top=${top}`);
    if (!popup) return null;

    let successReceived = false;
    const tempChannel = new BroadcastChannel('oauth_channel');
    tempChannel.onmessage = (e) => {
      if (e.data?.type === 'oauth-success') successReceived = true;
    };

    const interval = setInterval(() => {
      if (!popup || popup.closed) {
        clearInterval(interval);
        tempChannel.close();
        setTimeout(() => {
          if (!successReceived) { setGoogleLoading(false); setFacebookLoading(false); }
        }, 700);
      }
    }, 500);

    return popup;
  };

  const handleGoogleLogin = () => {
    // ✅ Terms guard — only enforced on signup, not login
    if (mode === 'signup' && !termsAgreed) {
      setTermsError('Please Read and agree to the Terms & Privacy to continue.');
      document.getElementById('terms-checkbox-signup')?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      return;
    }
    setTermsError('');
    setGoogleLoading(true); setLoginError('');
    const googleUrl = refCode
      ? `https://imselling-marketplace-production.up.railway.app/api/auth/google/?ref=${encodeURIComponent(refCode)}`
      : 'https://imselling-marketplace-production.up.railway.app/api/auth/google/';
    const popup = openOAuthPopup(googleUrl, 'Google Login');
    if (!popup) {
      setGoogleLoading(false);
      setLoginError('Popup blocked. Please allow popups for this site and try again.');
    }
  };

  const handleFacebookLogin = () => {
    // ✅ Terms guard — only enforced on signup, not login
    if (mode === 'signup' && !termsAgreed) {
      setTermsError('Please Read and agree to the Terms & Privacy to continue.');
      document.getElementById('terms-checkbox-signup')?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      return;
    }
    setTermsError('');
    setFacebookLoading(true); setLoginError('');
    const facebookUrl = refCode
      ? `https://imselling-marketplace-production.up.railway.app/api/auth/facebook/?ref=${encodeURIComponent(refCode)}`
      : 'https://imselling-marketplace-production.up.railway.app/api/auth/facebook/';
    const popup = openOAuthPopup(facebookUrl, 'Facebook Login');
    if (!popup) {
      setFacebookLoading(false);
      setLoginError('Popup blocked. Please allow popups for this site and try again.');
    }
  };

  // ── RESET HELPERS ─────────────────────────────────────────────────────────

  const resetSignupForm = () => {
    setSignupEmail(''); setSignupPhone(''); setSignupPassword(''); setSignupPasswordConfirm('');
    setSignupError(''); setVerificationSent(false); setVerificationCode('');
    setShowPassword(false); setShowConfirmPassword(false);
    setTermsAgreed(false); setTermsError('');
  };

  const resetLoginForm = () => {
    setLoginIdentifier(''); setLoginPassword(''); setLoginError('');
    setUseVerificationCode(false);
  };

  const handleClose = () => {
    resetLoginForm(); resetSignupForm();
    setMode('login'); setGoogleLoading(false); setFacebookLoading(false);
    onClose();
  };

  // ── RENDER ────────────────────────────────────────────────────────────────

  // Inline style applied to input wrapper when shaking
  const shakeStyle = shaking ? { animation: 'manualShake 0.6s ease' } : {};

  const modalContent = (
    <div className={styles.modalOverlay} onClick={handleClose}>
      <div className={styles.loginModal} onClick={(e) => e.stopPropagation()}>
        <button className={styles.closeBtn} onClick={handleClose}>×</button>
        <h2 className={styles.modalTitle}>Welcome to I'm Selling</h2>
        <p className={styles.modalSubtitle}>Buy or sell anything easily</p>

        {/* ── LOGIN MODE ── */}
        {mode === 'login' && (
          <>
            {!useVerificationCode ? (
              <form onSubmit={handlePasswordLogin}>
                {/* Shake wrapper around all manual inputs */}
                <div style={shakeStyle}>
                  <input
                    type="text"
                    className={styles.inputField}
                    placeholder="Email or Phone Number"
                    value={loginIdentifier}
                    onChange={(e) => { setLoginIdentifier(e.target.value); handleManualInputInteraction(); }}
                    onFocus={handleManualInputInteraction}
                    style={{ cursor: 'pointer' }}
                  />
                  <div className={styles.passwordWrapper}>
                    <input
                      type={showPassword ? 'text' : 'password'}
                      className={styles.inputField}
                      placeholder="Password"
                      value={loginPassword}
                      onChange={(e) => { setLoginPassword(e.target.value); handleManualInputInteraction(); }}
                      onFocus={handleManualInputInteraction}
                      style={{ cursor: 'pointer' }}
                    />
                    <button
                      type="button"
                      className={styles.togglePassword}
                      onClick={() => setShowPassword(!showPassword)}
                    >
                      {showPassword ? '👁️' : '👁️‍🗨️'}
                    </button>
                  </div>
                </div>

                {loginError && <div className={styles.errorMessage}>{loginError}</div>}

                <div className={styles.buttonGroup}>
                  <button type="submit" className={styles.continueBtn} disabled={googleLoading || facebookLoading}>
                    Login
                  </button>
                  <button
                    type="button"
                    onClick={() => { resetLoginForm(); setMode('signup'); }}
                    className={styles.secondaryBtn}
                    disabled={googleLoading || facebookLoading}
                  >
                    Sign Up
                  </button>
                </div>

                <button
                  type="button"
                  onClick={handleSendVerificationCode}
                  className={styles.forgotPassword}
                >
                  Forgot password?
                </button>
              </form>
            ) : (
              /* Forgot-password / OTP — also blocked */
              <form onSubmit={handleSendVerificationCode}>
                <div style={shakeStyle}>
                  <input
                    type="text"
                    className={styles.inputField}
                    placeholder="Email or Phone Number"
                    value={loginIdentifier}
                    onChange={(e) => { setLoginIdentifier(e.target.value); handleManualInputInteraction(); }}
                    onFocus={handleManualInputInteraction}
                  />
                </div>
                {loginError && <div className={styles.errorMessage}>{loginError}</div>}
                <button type="submit" className={styles.continueBtn} disabled={googleLoading || facebookLoading}>
                  Send Verification Code
                </button>
                <div className={styles.textCenter}>
                  <button
                    type="button"
                    className={styles.linkButton}
                    onClick={() => { setUseVerificationCode(false); setLoginError(''); }}
                  >
                    ← Back to password login
                  </button>
                </div>
              </form>
            )}
          </>
        )}

        {/* ── SIGNUP MODE ── */}
        {mode === 'signup' && (
          <>
            {!verificationSent ? (
              <form onSubmit={handleSendSignupVerification}>
                <div style={shakeStyle}>
                  <input
                    type="email"
                    className={styles.inputField}
                    placeholder="Email"
                    value={signupEmail}
                    onChange={(e) => { setSignupEmail(e.target.value); handleManualInputInteraction(); }}
                    onFocus={handleManualInputInteraction}
                  />
                  <input
                    type="tel"
                    className={styles.inputField}
                    placeholder="Phone Number"
                    value={signupPhone}
                    onChange={(e) => { setSignupPhone(e.target.value); handleManualInputInteraction(); }}
                    onFocus={handleManualInputInteraction}
                  />
                  <div className={styles.passwordWrapper}>
                    <input
                      type={showPassword ? 'text' : 'password'}
                      className={styles.inputField}
                      placeholder="Password (min 6 characters)"
                      value={signupPassword}
                      onChange={(e) => { setSignupPassword(e.target.value); handleManualInputInteraction(); }}
                      onFocus={handleManualInputInteraction}
                    />
                    <button type="button" className={styles.togglePassword} onClick={() => setShowPassword(!showPassword)}>
                      {showPassword ? '👁️' : '👁️‍🗨️'}
                    </button>
                  </div>
                  <div className={styles.passwordWrapper}>
                    <input
                      type={showConfirmPassword ? 'text' : 'password'}
                      className={styles.inputField}
                      placeholder="Confirm Password"
                      value={signupPasswordConfirm}
                      onChange={(e) => { setSignupPasswordConfirm(e.target.value); handleManualInputInteraction(); }}
                      onFocus={handleManualInputInteraction}
                    />
                    <button type="button" className={styles.togglePassword} onClick={() => setShowConfirmPassword(!showConfirmPassword)}>
                      {showConfirmPassword ? '👁️' : '👁️‍🗨️'}
                    </button>
                  </div>
                </div>

                {refCode && (
                  <div className={styles.verificationInfo}>
                    🎉 Signing up via referral link
                    <strong>Referral code: {refCode}</strong>
                  </div>
                )}

                {signupError && <div className={styles.errorMessage}>{signupError}</div>}

                {/* Terms checkbox */}
                <div id="terms-checkbox-signup" className={styles.termsCheckRow}>
                  <label className={styles.termsCheckLabel}>
                    <input
                      type="checkbox"
                      className={styles.termsCheckbox}
                      checked={termsAgreed}
                      onChange={(e) => { setTermsAgreed(e.target.checked); if (e.target.checked) setTermsError(''); }}
                    />
                    <span className={styles.termsCheckText}>
                      I have read and agree to the{' '}
                      <button type="button" className={styles.termsLink} onClick={() => setShowTerms(true)}>
                        Terms &amp; Privacy
                      </button>
                    </span>
                  </label>
                  {termsError && <div className={styles.termsErrorMsg}>{termsError}</div>}
                </div>

                <button type="submit" className={styles.continueBtn} disabled={googleLoading || facebookLoading}>
                  Send Verification Code
                </button>
                <div className={styles.textCenter}>
                  <button
                    type="button"
                    onClick={() => { resetSignupForm(); setMode('login'); }}
                    className={styles.linkButton}
                  >
                    Already have an account? Login
                  </button>
                </div>
              </form>
            ) : (
              /* OTP step — also blocked */
              <form onSubmit={handleSignup}>
                <div className={styles.verificationInfo}>
                  Enter the 6-digit code sent to
                  <strong>{signupEmail}</strong>
                </div>
                <div style={shakeStyle}>
                  <input
                    type="text"
                    className={`${styles.inputField} ${styles.codeInput}`}
                    placeholder="6-digit code"
                    value={verificationCode}
                    onChange={(e) => { setVerificationCode(e.target.value); handleManualInputInteraction(); }}
                    onFocus={handleManualInputInteraction}
                    maxLength="6"
                  />
                </div>
                {signupError && <div className={styles.errorMessage}>{signupError}</div>}
                <button type="submit" className={styles.continueBtn} disabled={googleLoading || facebookLoading}>
                  Create Account
                </button>
                <div className={styles.textCenter}>
                  <button type="button" onClick={() => setVerificationSent(false)} className={styles.linkButton}>
                    ← Back to signup
                  </button>
                </div>
              </form>
            )}
          </>
        )}

        {/* ── DIVIDER + SOCIAL ── */}
        <div className={styles.divider}><span>OR</span></div>

        <button
          className={`${styles.socialBtn} ${styles.googleBtn}`}
          type="button"
          onClick={handleGoogleLogin}
          disabled={googleLoading || facebookLoading}
        >
          {googleLoading ? (
            <><span className={styles.spinner}></span>Connecting...</>
          ) : (
            <>
              <svg className={styles.socialIconSvg} viewBox="0 0 24 24" width="20" height="20">
                <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
              </svg>
              Continue with Google
            </>
          )}
        </button>

        <button
          className={`${styles.socialBtn} ${styles.facebookBtn}`}
          type="button"
          onClick={handleFacebookLogin}
          disabled={googleLoading || facebookLoading}
        >
          {facebookLoading ? (
            <><span className={styles.spinner} style={{ borderTopColor: 'white' }}></span>Connecting...</>
          ) : (
            <>
              <svg className={styles.socialIconSvg} viewBox="0 0 24 24" width="20" height="20" fill="white">
                <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/>
              </svg>
              Continue with Facebook
            </>
          )}
        </button>

        {mode === 'login' && (
          <p className={styles.terms}>
            By continuing, you agree to our{" "}
            <button type="button" className={styles.termsLink} onClick={() => setShowTerms(true)}>
              Terms &amp; Privacy
            </button>
          </p>
        )}
      </div>

      {/* ✅ Terms & Privacy modal */}
      <TermsAndPrivacyModal
        isOpen={showTerms}
        onClose={() => setShowTerms(false)}
        onAccept={() => { setTermsAgreed(true); setTermsError(''); setShowTerms(false); }}
      />
    </div>
  );

  return createPortal(modalContent, document.body);
};

export default LoginModal;

















































// import React, { useState, useEffect, useRef } from 'react';
// import { createPortal } from 'react-dom';
// import { useAuthContext } from '../../../context/AuthContext';
// // import apiService from '../../../services/api'; // manual auth disabled
// import { ROUTES } from '../../../utils/constants';
// import styles from './LoginModal.module.css';
// import { saveAuth } from '../../../utils/authStorage'; // ✅ email-keyed storage
// import TermsAndPrivacyModal from './TermsAndPrivacyModal'; // ✅ Terms & Privacy modal

// const LoginModal = ({ isOpen, onClose, onOpenVerification, setCurrentPage }) => {
//   const { login, loginWithOAuth } = useAuthContext();

//   const [mode, setMode] = useState('login');
//   const [loginIdentifier, setLoginIdentifier] = useState('');
//   const [loginPassword, setLoginPassword] = useState('');
//   const [loginError, setLoginError] = useState('');
//   const [loginLoading, setLoginLoading] = useState(false);
//   const [showUseVerificationOption, setShowUseVerificationOption] = useState(false);
//   const [useVerificationCode, setUseVerificationCode] = useState(false);

//   const [signupEmail, setSignupEmail] = useState('');
//   const [signupPhone, setSignupPhone] = useState('');
//   const [signupPassword, setSignupPassword] = useState('');
//   const [signupPasswordConfirm, setSignupPasswordConfirm] = useState('');
//   const [signupError, setSignupError] = useState('');
//   // signupLoading — removed (manual auth disabled)
//   const [verificationSent, setVerificationSent] = useState(false);
//   const [verificationCode, setVerificationCode] = useState('');

//   const [showPassword, setShowPassword] = useState(false);
//   const [showConfirmPassword, setShowConfirmPassword] = useState(false);
//   const [googleLoading, setGoogleLoading] = useState(false);
//   const [facebookLoading, setFacebookLoading] = useState(false);
//   const [showTerms,       setShowTerms]       = useState(false); // ✅ Terms & Privacy modal
//   const [termsAgreed,     setTermsAgreed]     = useState(false); // ✅ Signup checkbox
//   const [termsError,      setTermsError]      = useState('');    // ✅ Checkbox nudge message

//   // ✅ FIX: Read ?ref= from URL every time the modal opens — NOT just on first
//   // mount. The old useState lazy initializer ran once when the component first
//   // mounted (before the user lands on the ?ref= URL if the modal is always in
//   // the DOM), so refCode was always '' for re-signups via referral links.
//   // Now we re-read window.location.search each time isOpen flips to true so
//   // the ref code is always current regardless of when the modal was mounted.
//   const [refCode, setRefCode] = useState('');

//   useEffect(() => {
//     if (!isOpen) return;
//     try {
//       const code = new URLSearchParams(window.location.search).get('ref') || '';
//       setRefCode(code);
//       // ✅ Only force signup mode if there's a ref code AND user doesn't exist yet.
//       // If user already has an account, checkUserExists will redirect them to login
//       // inside handleSendSignupVerification — no need to pre-check here.
//       if (code) {
//         setMode('signup');
//       }
//     } catch {
//       setRefCode('');
//     }
//     // eslint-disable-next-line react-hooks/exhaustive-deps
//   }, [isOpen]);

//   const loginRef            = useRef(login);
//   const loginWithOAuthRef   = useRef(loginWithOAuth);
//   const setCurrentPageRef   = useRef(setCurrentPage);
//   const onCloseRef          = useRef(onClose);

//   useEffect(() => {
//     loginRef.current          = login;
//     loginWithOAuthRef.current = loginWithOAuth;
//     setCurrentPageRef.current = setCurrentPage;
//     onCloseRef.current        = onClose;
//   }, [login, loginWithOAuth, setCurrentPage, onClose]);

//   // ✅ BroadcastChannel — receives OAuth success/failure from the popup
//   useEffect(() => {
//     const channel = new BroadcastChannel('oauth_channel');

//     channel.onmessage = (event) => {
//       console.log('📡 BroadcastChannel message:', event.data);
//       const { type, token, user, isAdmin, error } = event.data;

//       if (type === 'oauth-success') {
//         if (token || user) saveAuth(token, user);
//         if (loginWithOAuthRef.current) loginWithOAuthRef.current(event.data);

//         setGoogleLoading(false);
//         setFacebookLoading(false);
//         onCloseRef.current();

//         try {
//           const saved = localStorage.getItem('pendingProductView');
//           if (saved) {
//             window.dispatchEvent(new CustomEvent('restoreProductModal'));
//             localStorage.removeItem('pendingProductView');
//           }
//         } catch (e) { /* ignore */ }

//         setCurrentPageRef.current(
//           (user?.is_admin || isAdmin) ? ROUTES.ADMIN : ROUTES.HOME
//         );

//       } else if (type === 'oauth-error') {
//         setLoginError(error || 'Social login failed. Please try again.');
//         setGoogleLoading(false);
//         setFacebookLoading(false);
//       }
//     };

//     return () => channel.close();
//   }, []);

//   if (!isOpen) return null;

//   // ── Helpers ──────────────────────────────────────────────────────────────

//   // handlePostLoginRedirect — unused (manual auth disabled)
  // const handlePostLoginRedirect = () => {
//     try {
//       const saved = localStorage.getItem('pendingProductView');
//       if (saved) {
//         window.dispatchEvent(new CustomEvent('restoreProductModal'));
//         localStorage.removeItem('pendingProductView');
//       }
//     } catch (e) { console.error(e); }
//   };

//   const validatePassword = (p) => p.length < 6 ? 'Password must be at least 6 characters' : null;
//   const validateEmail    = (e) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(e) ? null : 'Please enter a valid email address';

//   const switchToSignup = (prefillEmail = '') => {
//     resetLoginForm();
//     if (prefillEmail) setSignupEmail(prefillEmail);
//     setMode('signup');
//   };

//   // ── LOGIN HANDLER ─────────────────────────────────────────────────────────

//   const handlePasswordLogin = async (e) => {
//     e.preventDefault();
//     if (!loginIdentifier || !loginPassword) {
//       setLoginError('Please enter both email and password');
//       return;
//     }

//     setLoginLoading(true);
//     setLoginError('');

//     try {
//       // ✅ Step 1: for email logins, check UserProfile existence FIRST.
//       //    If the user doesn't exist in UserProfile, auto-switch to Sign Up
//       //    instead of showing a confusing "wrong password" error.
//       const isEmail = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(loginIdentifier.trim());
//       if (isEmail) {
//         try {
//           const { exists } = await apiService.checkUserExists(loginIdentifier.trim());
//           if (!exists) {
//             setLoginLoading(false);
//             setLoginError('');
//             // Auto-switch to Sign Up with the email pre-filled
//             switchToSignup(loginIdentifier.trim());
//             return;
//           }
//         } catch {
//           // checkUserExists failed — continue to normal login, don't block
//         }
//       }

//       // ✅ Step 2: attempt password login
//       const result = await login(loginIdentifier, loginPassword);

//       if (result.success) {
//         handleClose();
//         handlePostLoginRedirect();
//         setCurrentPage(result.isAdmin ? ROUTES.ADMIN : ROUTES.HOME);

//       } else if (result.redirect_to_signup) {
//         // ✅ Backend explicitly says no account found → switch to Sign Up
//         setLoginLoading(false);
//         switchToSignup(loginIdentifier.trim());

//       } else {
//         setLoginError(result.error || 'Login failed. Please try again.');
//         setShowUseVerificationOption(true);
//       }

//     } catch (err) {
//       setLoginError('Network error. Please check your connection and try again.');
//       setShowUseVerificationOption(true);
//     } finally {
//       setLoginLoading(false);
//     }
//   };

//   // ── VERIFICATION CODE (forgot-password / OTP login) ───────────────────────

//   const handleSendVerificationCode = async (e) => {
//     if (e) e.preventDefault();
//     if (!loginIdentifier) { setLoginError('Please enter your email or phone number'); return; }
//     setLoginLoading(true); setLoginError('');
//     try {
//       const response = await apiService.sendVerificationCode(loginIdentifier);
//       if (response.code) {
//         onClose(); onOpenVerification(loginIdentifier);
//         alert(`Verification code sent to ${loginIdentifier}!\n\nFor testing: ${response.code}`);
//       } else { setLoginError(response.error || 'Failed to send verification code'); }
//     } catch (err) { setLoginError('Network error. Please check your connection and try again.'); }
//     finally { setLoginLoading(false); }
//   };

//   // ── SIGNUP STEP 1: Send OTP ───────────────────────────────────────────────

//   const handleSendSignupVerification = async (e) => {
//     e.preventDefault();
//     // ✅ Terms checkbox guard — must agree before proceeding
//     if (!termsAgreed) {
//       setTermsError('Please agree to the Terms & Privacy below to continue.');
//       // Scroll the checkbox into view
//       document.getElementById('terms-checkbox-signup')?.scrollIntoView({ behavior: 'smooth', block: 'center' });
//       return;
//     }
//     setTermsError('');
//     if (!signupEmail || !signupPhone || !signupPassword || !signupPasswordConfirm) {
//       setSignupError('All fields are required'); return;
//     }
//     const ee = validateEmail(signupEmail); if (ee) { setSignupError(ee); return; }
//     const pe = validatePassword(signupPassword); if (pe) { setSignupError(pe); return; }
//     if (signupPassword !== signupPasswordConfirm) { setSignupError('Passwords do not match'); return; }

//     setSignupLoading(true); setSignupError('');

//     try {
//       // ✅ Check UserProfile existence BEFORE sending an OTP.
//       //    Uses the same backend endpoint (UserProfile only) so a previously
//       //    deleted-then-recreated user is correctly treated as new.
//       const { exists } = await apiService.checkUserExists(signupEmail.trim());
//       if (exists) {
//         // ✅ User already has an account — switch to login mode with email pre-filled.
//         setSignupLoading(false);
//         resetSignupForm();
//         setLoginIdentifier(signupEmail.trim());
//         setMode('login');
//         return;
//       }

//       const response = await apiService.sendVerificationCode(signupEmail);
//       if (response.code) {
//         setVerificationSent(true);
//         alert(`Verification code sent to ${signupEmail}!\n\nFor testing: ${response.code}`);
//       } else {
//         setSignupError(response.error || 'Failed to send verification code');
//       }
//     } catch (err) {
//       setSignupError('Network error. Please check your connection and try again.');
//     } finally {
//       setSignupLoading(false);
//     }
//   };

//   // ── SIGNUP STEP 2: Create account ─────────────────────────────────────────

//   const handleSignup = async (e) => {
//     e.preventDefault();
//     if (!verificationCode) { setSignupError('Please enter the verification code'); return; }
//     if (verificationCode.length !== 6) { setSignupError('Verification code must be 6 digits'); return; }

//     setSignupLoading(true); setSignupError('');

//     try {
//       const response = await apiService.signup({
//         email: signupEmail,
//         phone: signupPhone,
//         password: signupPassword,
//         verificationCode,
//         // ✅ Always pass ref code — backend will wire the new user to the referrer.
//         //    Because signup now checks UserProfile only (not auth User table),
//         //    a deleted-then-recreated user gets here fresh and the referral fires.
//         ...(refCode ? { ref: refCode } : {}),
//       });

//       if (response.success) {
//         // ✅ Auto-login: signup already returns a token — no manual login step needed.
//         if (response.token || response.user) {
//           saveAuth(response.token, response.user);
//           if (loginWithOAuth) loginWithOAuth({ token: response.token, user: response.user });
//         }
//         handleClose();
//         handlePostLoginRedirect();
//         const isAdmin = response.user?.is_staff || response.user?.is_superuser || false;
//         setCurrentPage(isAdmin ? ROUTES.ADMIN : ROUTES.HOME);
//       } else {
//         setSignupError(response.error || 'Sign up failed. Please try again.');
//       }
//     } catch (err) {
//       setSignupError('Network error. Please check your connection and try again.');
//     } finally {
//       setSignupLoading(false);
//     }
//   };

//   // ── OAUTH ─────────────────────────────────────────────────────────────────

//   const openOAuthPopup = (url, name) => {
//     const width = 500, height = 600;
//     const left = window.screen.width / 2 - width / 2;
//     const top  = window.screen.height / 2 - height / 2;
//     const popup = window.open(url, name, `width=${width},height=${height},left=${left},top=${top}`);
//     if (!popup) return null;

//     let successReceived = false;
//     const tempChannel = new BroadcastChannel('oauth_channel');
//     tempChannel.onmessage = (e) => {
//       if (e.data?.type === 'oauth-success') successReceived = true;
//     };

//     const interval = setInterval(() => {
//       if (!popup || popup.closed) {
//         clearInterval(interval);
//         tempChannel.close();
//         setTimeout(() => {
//           if (!successReceived) { setGoogleLoading(false); setFacebookLoading(false); }
//         }, 700);
//       }
//     }, 500);

//     return popup;
//   };

//   const handleGoogleLogin = () => {
//     // ✅ Terms guard — only enforced on signup, not login
//     if (mode === 'signup' && !termsAgreed) {
//       setTermsError('Please agree to the Terms & Privacy below to continue.');
//       document.getElementById('terms-checkbox-signup')?.scrollIntoView({ behavior: 'smooth', block: 'center' });
//       return;
//     }
//     setTermsError('');
//     setGoogleLoading(true); setLoginError('');
//     const googleUrl = refCode
//       ? `https://imselling-marketplace-production.up.railway.app/api/auth/google/?ref=${encodeURIComponent(refCode)}`
//       : 'https://imselling-marketplace-production.up.railway.app/api/auth/google/';
//     const popup = openOAuthPopup(googleUrl, 'Google Login');
//     if (!popup) {
//       setGoogleLoading(false);
//       setLoginError('Popup blocked. Please allow popups for this site and try again.');
//     }
//   };

//   const handleFacebookLogin = () => {
//     // ✅ Terms guard — only enforced on signup, not login
//     if (mode === 'signup' && !termsAgreed) {
//       setTermsError('Please agree to the Terms & Privacy below to continue.');
//       document.getElementById('terms-checkbox-signup')?.scrollIntoView({ behavior: 'smooth', block: 'center' });
//       return;
//     }
//     setTermsError('');
//     setFacebookLoading(true); setLoginError('');
//     const facebookUrl = refCode
//       ? `https://imselling-marketplace-production.up.railway.app/api/auth/facebook/?ref=${encodeURIComponent(refCode)}`
//       : 'https://imselling-marketplace-production.up.railway.app/api/auth/facebook/';
//     const popup = openOAuthPopup(facebookUrl, 'Facebook Login');
//     if (!popup) {
//       setFacebookLoading(false);
//       setLoginError('Popup blocked. Please allow popups for this site and try again.');
//     }
//   };

//   // ── RESET HELPERS ─────────────────────────────────────────────────────────

//   const resetSignupForm = () => {
//     setSignupEmail(''); setSignupPhone(''); setSignupPassword(''); setSignupPasswordConfirm('');
//     setSignupError(''); setSignupLoading(false); setVerificationSent(false); setVerificationCode('');
//     setShowPassword(false); setShowConfirmPassword(false);
//     setTermsAgreed(false); setTermsError('');
//   };

//   const resetLoginForm = () => {
//     setLoginIdentifier(''); setLoginPassword(''); setLoginError('');
//     setLoginLoading(false); setShowUseVerificationOption(false); setUseVerificationCode(false);
//   };

//   const handleClose = () => {
//     resetLoginForm(); resetSignupForm();
//     setMode('login'); setGoogleLoading(false); setFacebookLoading(false);
//     onClose();
//   };

//   // ── RENDER ────────────────────────────────────────────────────────────────

//   const modalContent = (
//     <div className={styles.modalOverlay} onClick={handleClose}>
//       <div className={styles.loginModal} onClick={(e) => e.stopPropagation()}>
//         <button className={styles.closeBtn} onClick={handleClose}>×</button>
//         <h2 className={styles.modalTitle}>Welcome to I'm Selling</h2>
//         <p className={styles.modalSubtitle}>Buy or sell anything easily</p>

//         {/* ── LOGIN MODE ── */}
//         {mode === 'login' && (
//           <>
//             {!useVerificationCode ? (
//               <form onSubmit={handlePasswordLogin}>
//                 <input
//                   type="text"
//                   className={styles.inputField}
//                   placeholder="Email or Phone Number"
//                   value={loginIdentifier}
//                   onChange={(e) => {
//                     setLoginIdentifier(e.target.value);
//                     setLoginError('');
//                     setShowUseVerificationOption(false);
//                   }}
//                   disabled={loginLoading || googleLoading || facebookLoading}
//                   required
//                 />
//                 <div className={styles.passwordWrapper}>
//                   <input
//                     type={showPassword ? 'text' : 'password'}
//                     className={styles.inputField}
//                     placeholder="Password"
//                     value={loginPassword}
//                     onChange={(e) => {
//                       setLoginPassword(e.target.value);
//                       setLoginError('');
//                       setShowUseVerificationOption(false);
//                     }}
//                     disabled={loginLoading || googleLoading || facebookLoading}
//                     required
//                   />
//                   <button
//                     type="button"
//                     className={styles.togglePassword}
//                     onClick={() => setShowPassword(!showPassword)}
//                     disabled={loginLoading || googleLoading || facebookLoading}
//                   >
//                     {showPassword ? '👁️' : '👁️‍🗨️'}
//                   </button>
//                 </div>

//                 {loginError && <div className={styles.errorMessage}>{loginError}</div>}

//                 <div className={styles.buttonGroup}>
//                   <button
//                     type="submit"
//                     className={styles.continueBtn}
//                     disabled={loginLoading || googleLoading || facebookLoading}
//                   >
//                     {loginLoading
//                       ? <><span className={styles.spinner}></span>Logging in...</>
//                       : 'Login'}
//                   </button>
//                   <button
//                     type="button"
//                     onClick={() => { resetLoginForm(); setMode('signup'); }}
//                     className={styles.secondaryBtn}
//                     disabled={loginLoading || googleLoading || facebookLoading}
//                   >
//                     Sign Up
//                   </button>
//                 </div>

//                 <button
//                   type="button"
//                   onClick={() => setUseVerificationCode(true)}
//                   className={styles.forgotPassword}
//                   disabled={loginLoading || googleLoading || facebookLoading}
//                 >
//                   Forgot password?
//                 </button>

//                 {showUseVerificationOption && !loginLoading && (
//                   <div className={styles.textCenter}>
//                     <button
//                       type="button"
//                       onClick={() => setUseVerificationCode(true)}
//                       className={styles.linkButton}
//                     >
//                       Use verification code instead
//                     </button>
//                   </div>
//                 )}
//               </form>
//             ) : (
//               /* Forgot-password / OTP login */
//               <form onSubmit={handleSendVerificationCode}>
//                 <input
//                   type="text"
//                   className={styles.inputField}
//                   placeholder="Email or Phone Number"
//                   value={loginIdentifier}
//                   onChange={(e) => { setLoginIdentifier(e.target.value); setLoginError(''); }}
//                   disabled={loginLoading}
//                   required
//                 />
//                 {loginError && <div className={styles.errorMessage}>{loginError}</div>}
//                 <button type="submit" className={styles.continueBtn} disabled={loginLoading}>
//                   {loginLoading
//                     ? <><span className={styles.spinner}></span>Sending...</>
//                     : 'Send Verification Code'}
//                 </button>
//                 <div className={styles.textCenter}>
//                   <button
//                     type="button"
//                     className={styles.linkButton}
//                     disabled={loginLoading}
//                     onClick={() => {
//                       setUseVerificationCode(false);
//                       setLoginError('');
//                       setShowUseVerificationOption(false);
//                     }}
//                   >
//                     ← Back to password login
//                   </button>
//                 </div>
//               </form>
//             )}
//           </>
//         )}

//         {/* ── SIGNUP MODE ── */}
//         {mode === 'signup' && (
//           <>
//             {!verificationSent ? (
//               <form onSubmit={handleSendSignupVerification}>
//                 <input
//                   type="email"
//                   className={styles.inputField}
//                   placeholder="Email"
//                   value={signupEmail}
//                   onChange={(e) => { setSignupEmail(e.target.value); setSignupError(''); }}
//                   disabled={signupLoading}
//                   required
//                 />
//                 <input
//                   type="tel"
//                   className={styles.inputField}
//                   placeholder="Phone Number"
//                   value={signupPhone}
//                   onChange={(e) => { setSignupPhone(e.target.value); setSignupError(''); }}
//                   disabled={signupLoading}
//                   required
//                 />
//                 <div className={styles.passwordWrapper}>
//                   <input
//                     type={showPassword ? 'text' : 'password'}
//                     className={styles.inputField}
//                     placeholder="Password (min 6 characters)"
//                     value={signupPassword}
//                     onChange={(e) => { setSignupPassword(e.target.value); setSignupError(''); }}
//                     disabled={signupLoading}
//                     required
//                   />
//                   <button
//                     type="button"
//                     className={styles.togglePassword}
//                     onClick={() => setShowPassword(!showPassword)}
//                     disabled={signupLoading}
//                   >
//                     {showPassword ? '👁️' : '👁️‍🗨️'}
//                   </button>
//                 </div>
//                 <div className={styles.passwordWrapper}>
//                   <input
//                     type={showConfirmPassword ? 'text' : 'password'}
//                     className={styles.inputField}
//                     placeholder="Confirm Password"
//                     value={signupPasswordConfirm}
//                     onChange={(e) => { setSignupPasswordConfirm(e.target.value); setSignupError(''); }}
//                     disabled={signupLoading}
//                     required
//                   />
//                   <button
//                     type="button"
//                     className={styles.togglePassword}
//                     onClick={() => setShowConfirmPassword(!showConfirmPassword)}
//                     disabled={signupLoading}
//                   >
//                     {showConfirmPassword ? '👁️' : '👁️‍🗨️'}
//                   </button>
//                 </div>

//                 {refCode && (
//                   <div className={styles.verificationInfo}>
//                     🎉 Signing up via referral link
//                     <strong>Referral code: {refCode}</strong>
//                   </div>
//                 )}

//                 {signupError && <div className={styles.errorMessage}>{signupError}</div>}

//                 {/* ✅ Terms checkbox — must be ticked before any signup action */}
//                 <div id="terms-checkbox-signup" className={styles.termsCheckRow}>
//                   <label className={styles.termsCheckLabel}>
//                     <input
//                       type="checkbox"
//                       className={styles.termsCheckbox}
//                       checked={termsAgreed}
//                       onChange={(e) => { setTermsAgreed(e.target.checked); if (e.target.checked) setTermsError(''); }}
//                       disabled={signupLoading}
//                     />
//                     <span className={styles.termsCheckText}>
//                       I have read and agree to the{' '}
//                       <button
//                         type="button"
//                         className={styles.termsLink}
//                         onClick={() => setShowTerms(true)}
//                       >
//                         Terms &amp; Privacy
//                       </button>
//                     </span>
//                   </label>
//                   {termsError && (
//                     <div className={styles.termsErrorMsg}>{termsError}</div>
//                   )}
//                 </div>

//                 <button type="submit" className={styles.continueBtn} disabled={signupLoading}>
//                   {signupLoading
//                     ? <><span className={styles.spinner}></span>Sending...</>
//                     : 'Send Verification Code'}
//                 </button>
//                 <div className={styles.textCenter}>
//                   <button
//                     type="button"
//                     onClick={() => { resetSignupForm(); setMode('login'); }}
//                     className={styles.linkButton}
//                     disabled={signupLoading}
//                   >
//                     Already have an account? Login
//                   </button>
//                 </div>
//               </form>
//             ) : (
//               /* OTP verification step */
//               <form onSubmit={handleSignup}>
//                 <div className={styles.verificationInfo}>
//                   Enter the 6-digit code sent to
//                   <strong>{signupEmail}</strong>
//                 </div>
//                 <input
//                   type="text"
//                   className={`${styles.inputField} ${styles.codeInput}`}
//                   placeholder="6-digit code"
//                   value={verificationCode}
//                   onChange={(e) => { setVerificationCode(e.target.value); setSignupError(''); }}
//                   maxLength="6"
//                   disabled={signupLoading}
//                   required
//                 />
//                 {signupError && <div className={styles.errorMessage}>{signupError}</div>}
//                 <button type="submit" className={styles.continueBtn} disabled={signupLoading}>
//                   {signupLoading
//                     ? <><span className={styles.spinner}></span>Creating Account...</>
//                     : 'Create Account'}
//                 </button>
//                 <div className={styles.textCenter}>
//                   <button
//                     type="button"
//                     onClick={() => setVerificationSent(false)}
//                     className={styles.linkButton}
//                     disabled={signupLoading}
//                   >
//                     ← Back to signup
//                   </button>
//                 </div>
//               </form>
//             )}
//           </>
//         )}

//         {/* ── DIVIDER + SOCIAL ── */}
//         <div className={styles.divider}><span>OR</span></div>

//         <button
//           className={`${styles.socialBtn} ${styles.googleBtn}`}
//           type="button"
//           onClick={handleGoogleLogin}
//           disabled={googleLoading || facebookLoading || loginLoading || signupLoading}
//         >
//           {googleLoading ? (
//             <><span className={styles.spinner}></span>Connecting...</>
//           ) : (
//             <>
//               <svg className={styles.socialIconSvg} viewBox="0 0 24 24" width="20" height="20">
//                 <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
//                 <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
//                 <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
//                 <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
//               </svg>
//               Continue with Google
//             </>
//           )}
//         </button>

//         <button
//           className={`${styles.socialBtn} ${styles.facebookBtn}`}
//           type="button"
//           onClick={handleFacebookLogin}
//           disabled={googleLoading || facebookLoading || loginLoading || signupLoading}
//         >
//           {facebookLoading ? (
//             <><span className={styles.spinner} style={{ borderTopColor: 'white' }}></span>Connecting...</>
//           ) : (
//             <>
//               <svg className={styles.socialIconSvg} viewBox="0 0 24 24" width="20" height="20" fill="white">
//                 <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/>
//               </svg>
//               Continue with Facebook
//             </>
//           )}
//         </button>

//         {/* On login mode: show the standard "By continuing" reminder.
//              On signup mode: the checkbox above already handles this. */}
//         {mode === 'login' && (
//           <p className={styles.terms}>
//             By continuing, you agree to our{" "}
//             <button
//               type="button"
//               className={styles.termsLink}
//               onClick={() => setShowTerms(true)}
//             >
//               Terms &amp; Privacy
//             </button>
//           </p>
//         )}
//       </div>

//       {/* ✅ Terms & Privacy modal — opened when user clicks the link */}
//       <TermsAndPrivacyModal
//         isOpen={showTerms}
//         onClose={() => setShowTerms(false)}
//       />
//     </div>
//   );

//   return createPortal(modalContent, document.body);
// };

// export default LoginModal;



























